import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Switch,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Chip,
  Container,
  CircularProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Rating,
} from '@mui/material';
import { 
  Edit as EditIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  Article as ArticleIcon,
  Feedback as FeedbackIcon,
  Reply as ReplyIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config';
import { motion } from 'framer-motion';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ padding: '20px 0' }}>
      {value === index && children}
    </div>
  );
}

function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [responseDialog, setResponseDialog] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackFilters, setFeedbackFilters] = useState({
    status: '',
    priority: '',
    type: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, postsRes, analyticsRes, feedbackRes, feedbackStatsRes] = await Promise.all([
        axios.get(`${getApiUrl()}/admin/users`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }),
        axios.get(`${getApiUrl()}/admin/posts`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }),
        axios.get(`${getApiUrl()}/admin/analytics`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }),
        axios.get(`${getApiUrl()}/admin/feedback`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          params: feedbackFilters
        }),
        axios.get(`${getApiUrl()}/admin/feedback/stats`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        })
      ]);

      setUsers(usersRes.data);
      setPosts(postsRes.data);
      setAnalytics(analyticsRes.data);
      setFeedback(feedbackRes.data);
      setFeedbackStats(feedbackStatsRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    try {
      await axios.put(`${getApiUrl()}/admin/users/${userId}`, {
        is_admin: !currentStatus,
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      fetchData(); // Refresh data
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleResponseSubmit = async () => {
    try {
      await axios.post(
        `${getApiUrl()}/admin/feedback/${selectedFeedback.id}/respond`,
        {
          response: responseText,
          admin_notes: adminNotes
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setResponseDialog(false);
      setResponseText('');
      setAdminNotes('');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send response');
    }
  };

  const handleStatusUpdate = async (feedbackId, status, priority) => {
    try {
      await axios.put(
        `${getApiUrl()}/admin/feedback/${feedbackId}/status`,
        { status, priority },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'error',
      high: 'warning',
      medium: 'info',
      low: 'success'
    };
    return colors[priority] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'info',
      in_progress: 'warning',
      completed: 'success',
      wont_fix: 'error'
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          background: 'linear-gradient(145deg, #f6f8ff 0%, #f0f4ff 100%)',
          borderRadius: 4,
          p: 4,
          minHeight: '90vh',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box mb={4}>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Admin Panel
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Manage your platform's users and content
            </Typography>
          </Box>

          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            sx={{
              mb: 3,
              '& .MuiTab-root': {
                textTransform: 'none',
                minWidth: 120,
                fontSize: '1rem',
              },
              '& .Mui-selected': {
                fontWeight: 'bold',
              },
            }}
          >
            <Tab icon={<TimelineIcon />} label="Dashboard" iconPosition="start" />
            <Tab icon={<GroupIcon />} label="Users" iconPosition="start" />
            <Tab icon={<ArticleIcon />} label="Posts" iconPosition="start" />
            <Tab icon={<FeedbackIcon />} label="Feedback" iconPosition="start" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {analytics && (
              <Grid container spacing={3}>
                {/* User Stats */}
                <Grid item xs={12} md={4}>
                  <motion.div whileHover={{ y: -4 }} transition={{ type: "spring", stiffness: 300 }}>
                    <Paper
                      elevation={2}
                      sx={{
                        borderRadius: 3,
                        p: 3,
                        height: '100%',
                        background: 'white',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={2}>
                        <Box
                          sx={{
                            background: 'rgba(33, 150, 243, 0.1)',
                            borderRadius: '50%',
                            p: 1,
                            mr: 2,
                            color: 'primary.main',
                          }}
                        >
                          <PersonIcon />
                        </Box>
                        <Typography variant="h6">User Statistics</Typography>
                      </Box>
                      <List>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant="h4" color="primary.main" fontWeight="bold">
                                {analytics.total_users}
                              </Typography>
                            }
                            secondary="Total Users"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant="h4" color="success.main" fontWeight="bold">
                                {analytics.user_stats.active_users}
                              </Typography>
                            }
                            secondary="Active Users"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant="h4" color="info.main" fontWeight="bold">
                                {analytics.user_stats.admin_users}
                              </Typography>
                            }
                            secondary="Admin Users"
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </motion.div>
                </Grid>

                {/* Post Stats */}
                <Grid item xs={12} md={4}>
                  <motion.div whileHover={{ y: -4 }} transition={{ type: "spring", stiffness: 300 }}>
                    <Paper
                      elevation={2}
                      sx={{
                        borderRadius: 3,
                        p: 3,
                        height: '100%',
                        background: 'white',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={2}>
                        <Box
                          sx={{
                            background: 'rgba(76, 175, 80, 0.1)',
                            borderRadius: '50%',
                            p: 1,
                            mr: 2,
                            color: 'success.main',
                          }}
                        >
                          <DescriptionIcon />
                        </Box>
                        <Typography variant="h6">Post Statistics</Typography>
                      </Box>
                      <List>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant="h4" color="primary.main" fontWeight="bold">
                                {analytics.total_posts}
                              </Typography>
                            }
                            secondary="Total Posts"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant="h4" color="success.main" fontWeight="bold">
                                {analytics.post_stats.published}
                              </Typography>
                            }
                            secondary="Published Posts"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant="h4" color="warning.main" fontWeight="bold">
                                {analytics.post_stats.deleted}
                              </Typography>
                            }
                            secondary="Deleted Posts"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant="h4" color="info.main" fontWeight="bold">
                                {analytics.post_stats.scheduled}
                              </Typography>
                            }
                            secondary="Scheduled Posts"
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </motion.div>
                </Grid>

                {/* Recent Posts */}
                <Grid item xs={12}>
                  <Paper
                    elevation={2}
                    sx={{
                      borderRadius: 3,
                      overflow: 'hidden',
                    }}
                  >
                    <Box p={3}>
                      <Box display="flex" alignItems="center" mb={3}>
                        <Box
                          sx={{
                            background: 'rgba(33, 150, 243, 0.1)',
                            borderRadius: '50%',
                            p: 1,
                            mr: 2,
                            color: 'primary.main',
                          }}
                        >
                          <TrendingUpIcon />
                        </Box>
                        <Typography variant="h6">Recent Posts</Typography>
                      </Box>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Title</TableCell>
                              <TableCell>Author</TableCell>
                              <TableCell>Created At</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Views</TableCell>
                              <TableCell align="right">Comments</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {analytics.recent_posts.map((post) => (
                              <TableRow key={post.id} hover>
                                <TableCell>{post.title}</TableCell>
                                <TableCell>{post.author}</TableCell>
                                <TableCell>
                                  {new Date(post.created_at).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={post.status}
                                    size="small"
                                    sx={{
                                      backgroundColor: 
                                        post.status === 'published' ? 'success.light' :
                                        post.status === 'scheduled' ? 'info.light' :
                                        'warning.light',
                                      color: 
                                        post.status === 'published' ? 'success.dark' :
                                        post.status === 'scheduled' ? 'info.dark' :
                                        'warning.dark',
                                      fontWeight: 500,
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right">{post.views}</TableCell>
                                <TableCell align="right">{post.comments}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Paper
              elevation={2}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <Box p={3}>
                <Box display="flex" alignItems="center" mb={3}>
                  <Box
                    sx={{
                      background: 'rgba(33, 150, 243, 0.1)',
                      borderRadius: '50%',
                      p: 1,
                      mr: 2,
                      color: 'primary.main',
                    }}
                  >
                    <GroupIcon />
                  </Box>
                  <Typography variant="h6">User Management</Typography>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Created At</TableCell>
                        <TableCell>Admin Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Avatar 
                                sx={{ 
                                  width: 32, 
                                  height: 32, 
                                  mr: 2,
                                  bgcolor: 'primary.main',
                                }}
                              >
                                {user.email[0].toUpperCase()}
                              </Avatar>
                              {user.email}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {new Date(user.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Switch
                              checked={user.is_admin}
                              onChange={() => handleToggleAdmin(user.id, user.is_admin)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              sx={{
                                '&:hover': {
                                  color: 'primary.main',
                                  bgcolor: 'primary.light',
                                },
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Paper
              elevation={2}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <Box p={3}>
                <Box display="flex" alignItems="center" mb={3}>
                  <Box
                    sx={{
                      background: 'rgba(33, 150, 243, 0.1)',
                      borderRadius: '50%',
                      p: 1,
                      mr: 2,
                      color: 'primary.main',
                    }}
                  >
                    <ArticleIcon />
                  </Box>
                  <Typography variant="h6">Post Management</Typography>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>User ID</TableCell>
                        <TableCell>Created At</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {posts.map((post) => (
                        <TableRow key={post.id} hover>
                          <TableCell>{post.title}</TableCell>
                          <TableCell>{post.user_id}</TableCell>
                          <TableCell>
                            {new Date(post.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={post.status}
                              size="small"
                              sx={{
                                backgroundColor: 
                                  post.status === 'published' ? 'success.light' :
                                  post.status === 'scheduled' ? 'info.light' :
                                  'warning.light',
                                color: 
                                  post.status === 'published' ? 'success.dark' :
                                  post.status === 'scheduled' ? 'info.dark' :
                                  'warning.dark',
                                fontWeight: 500,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={3}>
              {/* Feedback Stats Cards */}
              <Grid item xs={12} md={4}>
                <motion.div whileHover={{ y: -4 }} transition={{ type: "spring", stiffness: 300 }}>
                  <Paper
                    elevation={2}
                    sx={{
                      borderRadius: 3,
                      p: 3,
                      height: '100%',
                      background: 'white',
                    }}
                  >
                    <Typography variant="h6" gutterBottom>Feedback Overview</Typography>
                    {feedbackStats && (
                      <List>
                        <ListItem>
                          <ListItemText
                            primary={<Typography variant="h4" color="primary">{feedbackStats.total_feedback}</Typography>}
                            secondary="Total Feedback"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary={<Typography variant="h4" color="warning.main">{feedbackStats.unresponded_count}</Typography>}
                            secondary="Awaiting Response"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant="h4" color="success.main">
                                {feedbackStats.avg_rating ? feedbackStats.avg_rating.toFixed(1) : 'N/A'}
                              </Typography>
                            }
                            secondary="Average Rating"
                          />
                        </ListItem>
                      </List>
                    )}
                  </Paper>
                </motion.div>
              </Grid>

              {/* Feedback Table */}
              <Grid item xs={12}>
                <Paper
                  elevation={2}
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                  }}
                >
                  <Box p={3}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                      <Typography variant="h6">Feedback Management</Typography>
                      <Box display="flex" gap={2}>
                        <TextField
                          select
                          size="small"
                          label="Status"
                          value={feedbackFilters.status}
                          onChange={(e) => setFeedbackFilters({ ...feedbackFilters, status: e.target.value })}
                          sx={{ minWidth: 120 }}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="new">New</MenuItem>
                          <MenuItem value="in_progress">In Progress</MenuItem>
                          <MenuItem value="completed">Completed</MenuItem>
                          <MenuItem value="wont_fix">Won't Fix</MenuItem>
                        </TextField>
                        <TextField
                          select
                          size="small"
                          label="Priority"
                          value={feedbackFilters.priority}
                          onChange={(e) => setFeedbackFilters({ ...feedbackFilters, priority: e.target.value })}
                          sx={{ minWidth: 120 }}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="critical">Critical</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="low">Low</MenuItem>
                        </TextField>
                        <Button
                          variant="contained"
                          onClick={fetchData}
                        >
                          Apply Filters
                        </Button>
                      </Box>
                    </Box>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Content</TableCell>
                            <TableCell>Rating</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Priority</TableCell>
                            <TableCell>Created At</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {feedback.map((item) => (
                            <TableRow key={item.id} hover>
                              <TableCell>{item.user_email}</TableCell>
                              <TableCell>
                                <Chip
                                  label={item.feedback_type}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell sx={{ maxWidth: 200 }}>
                                <Typography noWrap>{item.content}</Typography>
                              </TableCell>
                              <TableCell>
                                {item.rating ? (
                                  <Rating value={item.rating} readOnly size="small" />
                                ) : (
                                  'N/A'
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={item.status}
                                  size="small"
                                  color={getStatusColor(item.status)}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={item.priority}
                                  size="small"
                                  color={getPriorityColor(item.priority)}
                                />
                              </TableCell>
                              <TableCell>
                                {new Date(item.created_at).toLocaleDateString()}
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  onClick={() => {
                                    setSelectedFeedback(item);
                                    setResponseDialog(true);
                                  }}
                                  color="primary"
                                >
                                  <ReplyIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Response Dialog */}
          <Dialog
            open={responseDialog}
            onClose={() => setResponseDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Respond to Feedback</DialogTitle>
            <DialogContent>
              {selectedFeedback && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Original Feedback:
                  </Typography>
                  <Typography paragraph>{selectedFeedback.content}</Typography>
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Your Response"
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Admin Notes (Internal)"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        select
                        fullWidth
                        label="Status"
                        value={selectedFeedback.status}
                        onChange={(e) => handleStatusUpdate(selectedFeedback.id, e.target.value, selectedFeedback.priority)}
                      >
                        <MenuItem value="new">New</MenuItem>
                        <MenuItem value="in_progress">In Progress</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="wont_fix">Won't Fix</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        select
                        fullWidth
                        label="Priority"
                        value={selectedFeedback.priority}
                        onChange={(e) => handleStatusUpdate(selectedFeedback.id, selectedFeedback.status, e.target.value)}
                      >
                        <MenuItem value="critical">Critical</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="low">Low</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setResponseDialog(false)}>Cancel</Button>
              <Button
                onClick={handleResponseSubmit}
                variant="contained"
                disabled={!responseText.trim()}
              >
                Send Response
              </Button>
            </DialogActions>
          </Dialog>
        </motion.div>
      </Box>
    </Container>
  );
}

export default AdminPanel; 